import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock } from "lucide-react";
import {
  AppButton,
  AppModal,
  Callout,
  FieldGroup,
  FormField,
  Input,
  Radio,
  Textarea,
} from "../../ui";
import apiClient from "../../../utils/apiClient";
import { dayKey, formatDay, labelOf, nounOf, taughtDayKey } from "./scheduleFormat";

const FORM_ID = "syllabus-reschedule-form";
const REASON_MAX = 300;
const PREVIEW_DELAY_MS = 300;

const pluralOf = (noun) => (noun === "class" ? "classes" : `${noun}s`);

/** The two ways to move, worded for this plan's unit — day, class or session. */
const modesFor = (noun) => [
  {
    value: "cascade",
    label: `This ${noun} and every ${noun} after it`,
    description: `Later ${pluralOf(noun)} follow on the next class days, in order, skipping holidays. Use this when a class did not happen.`,
  },
  {
    value: "single",
    label: `Only this ${noun}`,
    description: `Nothing else moves — for a one-off swap. It cannot be moved past the next ${noun}.`,
  },
];

/**
 * RescheduleDialog — move one day / class / session of a batch's plan, and
 * usually every one after it.
 *
 * The server plans the move (vc_be utils/syllabusSchedule); this dialog shows
 * that plan before anything is saved, and saves only against the plan it
 * showed: the save carries the preview's fingerprint, and a schedule someone
 * changed in the meantime comes back as a new preview to read again.
 *
 * Props:
 *   show / onHide
 *   batchId
 *   session      — the row to move ({ dayNumber, label, title, plannedDate, status })
 *   initialDate  — 'YYYY-MM-DD' to start from (a suggested resume date)
 *   today        — the server's IST day; dates before it are refused
 *   canEditBatch — show "Edit batch" when the plan runs past the batch's end
 *   onSaved      — (message) => void, after the days have moved
 */
const RescheduleDialog = ({ show, onHide, batchId, session, initialDate, today, canEditBatch = false, onSaved }) => {
  const navigate = useNavigate();
  const minDate = today || taughtDayKey(new Date());
  const [date, setDate] = useState("");
  const [mode, setMode] = useState("cascade");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [refusal, setRefusal] = useState("");
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [failure, setFailure] = useState("");
  const requestRef = useRef(0);

  const dayNumber = session?.dayNumber;
  const planned = dayKey(session?.plannedDate);

  // Keyed on strings, not on `session`: a refresh behind the open dialog hands
  // it a new object for the same day, and that must not wipe what was typed.
  useEffect(() => {
    if (!show) return;
    setDate(initialDate || (planned && planned >= minDate ? planned : minDate));
    setMode("cascade");
    setReason("");
    setPreview(null);
    setRefusal("");
    setNotice("");
    setFailure("");
    setSaving(false);
  }, [show, dayNumber, planned, initialDate, minDate]);

  // Ask the server what this move would do, a moment after the last edit.
  useEffect(() => {
    if (!show || !dayNumber) return undefined;
    const id = (requestRef.current += 1);
    if (!date) {
      setPreview(null);
      setRefusal("Choose the new date.");
      setChecking(false);
      return undefined;
    }
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/reschedule/preview`, {
          fromDay: dayNumber,
          date,
          mode,
        });
        if (requestRef.current !== id) return;
        setPreview(data);
        setRefusal("");
      } catch (err) {
        if (requestRef.current !== id) return;
        setPreview(null);
        setRefusal(err?.response?.data?.message || "The new dates couldn’t be worked out. Check your connection and try again.");
      } finally {
        if (requestRef.current === id) setChecking(false);
      }
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [show, batchId, dayNumber, date, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!preview || checking || saving) return;
    setSaving(true);
    setFailure("");
    setNotice("");
    try {
      const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/reschedule`, {
        fromDay: dayNumber,
        date,
        mode,
        reason: reason.trim(),
        fingerprint: preview.fingerprint,
      });
      // The dialog closes on success, so there is no state left to update here.
      onSaved?.(data.message || "Syllabus dates updated.");
      onHide();
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.errorCode === "SCHEDULE_CHANGED" && body.preview) {
        setPreview(body.preview);
        setNotice(body.message);
      } else {
        setFailure(body.message || "The new dates weren’t saved. Check your connection and try again.");
      }
      setSaving(false);
    }
  };

  if (!session) return null;

  const changes = preview?.changes || [];
  const noun = nounOf(session);
  const Unit = noun.charAt(0).toUpperCase() + noun.slice(1);
  const label = labelOf(session);
  const followers = changes.filter((c) => c.dayNumber !== dayNumber).length;

  return (
    <AppModal
      show={show}
      onHide={saving ? undefined : onHide}
      closeOnOverlay={!saving}
      size="md"
      title={`Reschedule ${label}`}
      icon={<CalendarClock size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onHide} disabled={saving}>Cancel</AppButton>
          <AppButton type="submit" form={FORM_ID} disabled={!preview || checking || saving}>
            {saving ? "Saving…" : "Save new dates"}
          </AppButton>
        </div>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="tw-flex tw-flex-col tw-gap-5">
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">
          <span className="tw-font-semibold tw-text-content">{session.title}</span>
          {planned ? ` — planned for ${formatDay(planned)}` : " — no date planned yet"}
          {session.status === "in_progress" ? ", in progress now" : ""}.
        </p>

        {failure && <Callout tone="danger" role="alert">{failure}</Callout>}

        <FormField label="New date" required hint="Today or later.">
          <Input
            type="date"
            size="lg"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormField>

        <FieldGroup legend="What moves">
          <div className="tw-grid tw-gap-2">
            {modesFor(noun).map((m) => (
              <Radio
                key={m.value}
                variant="card"
                name="reschedule-mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => setMode(m.value)}
                labelClassName="tw-font-semibold"
                label={m.label}
                description={m.description}
              />
            ))}
          </div>
        </FieldGroup>

        <FormField
          label="Reason (optional)"
          hint={reason.length > REASON_MAX - 40 ? `${REASON_MAX - reason.length} characters left` : "Kept in the batch’s schedule history — “Trainer on leave 15–16 Oct”."}
        >
          <Textarea rows={2} value={reason} maxLength={REASON_MAX} onChange={(e) => setReason(e.target.value)} />
        </FormField>

        <section aria-live="polite" className="tw-flex tw-flex-col tw-gap-2">
          <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">What will change</h3>

          {checking && <p className="tw-m-0 tw-text-xs tw-text-content-muted">Working out the new dates…</p>}
          {!checking && refusal && <Callout tone="danger">{refusal}</Callout>}
          {notice && <Callout tone="warning">{notice}</Callout>}

          {!checking && preview && (
            <>
              {(preview.warnings || []).map((w) => (
                <Callout
                  key={w.code}
                  tone="warning"
                  action={w.code === "PAST_BATCH_END" && canEditBatch ? (
                    <AppButton size="xs" variant="outline" onClick={() => navigate(`/batches/edit/${batchId}`)}>
                      Edit batch
                    </AppButton>
                  ) : null}
                >
                  {w.message}
                  {w.code === "PAST_BATCH_END" && !canEditBatch ? " Ask an admin to extend it." : ""}
                </Callout>
              ))}

              <p className="tw-m-0 tw-text-xs tw-text-content-muted">
                {followers > 0
                  ? `${label} and ${followers} later ${followers === 1 ? noun : pluralOf(noun)} move.`
                  : `Only ${label} moves.`}
                {preview.lastPlannedDate ? ` The last ${noun} is then ${formatDay(preview.lastPlannedDate)}.` : ""}
              </p>

              <ol className="tw-m-0 tw-max-h-64 tw-list-none tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-0">
                {changes.map((c) => (
                  <li
                    key={c.dayNumber}
                    className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-2 last:tw-border-b-0"
                  >
                    <span className="tw-w-14 tw-shrink-0 tw-text-xs tw-font-semibold tw-text-content-muted">{Unit} {c.dayNumber}</span>
                    <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.8125rem] tw-text-content" title={c.title}>{c.title}</span>
                    <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-text-xs">
                      <span className="tw-text-content-subtle tw-line-through">{c.from ? formatDay(c.from) : "—"}</span>
                      <ArrowRight size={12} aria-hidden="true" className="tw-text-content-subtle" />
                      <span className="tw-sr-only">moves to</span>
                      <span className="tw-font-semibold tw-text-content">{formatDay(c.to)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </section>
      </form>
    </AppModal>
  );
};

export default RescheduleDialog;
